import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Grid2,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { toast } from 'sonner';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import { Link } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from "../common/Config";
import '../common/commonStyles.css';


const StockTakeHistory = () => {
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");
  const [stockHistory, setStockHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState({ el: null, item: null });

  const table = useTable({
    defaultRowsPerPage: 10,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'start_date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchStockHistory = async () => {
    const params = new URLSearchParams();
    params.append('draw', 1);
    params.append('start', 0);
    params.append('length', 5000);

    const apiUrl = `${API_URL}/product/get_stock_take_history?${params.toString()}`;

    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stock take history');
      }
      const data = await response.json();
      setStockHistory(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      toast.error('Failed to load stock take history');
      setStockHistory([]);
    }
  };

  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) {
      return stockHistory;
    }
    const query = searchQuery.toLowerCase();
    return stockHistory.filter((item) => {
      const uniqueId = (item.uniqueStockId || '').toLowerCase();
      const categories = (item.categories || '').toLowerCase();
      const brands = (item.brands || '').toLowerCase();
      const startDate = (item.start_date || '').toLowerCase();
      const endDate = (item.end_date || '').toLowerCase();
      return (
        uniqueId.includes(query) ||
        categories.includes(query) ||
        brands.includes(query) ||
        startDate.includes(query) ||
        endDate.includes(query)
      );
    });
  }, [stockHistory, searchQuery]);

  useEffect(() => {
    fetchStockHistory();

    document.title = 'Stock Take History';
    return () => {
      document.title = 'Ebusso';
    };
  }, []);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [searchQuery]);

  const handleMenuOpen = (event, item) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, item });
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, item: null });
  };

  const handleMenuAction = (action, item) => {
    handleMenuClose();
    if (action === 'view') {
      window.location.href = `${BASE_URL}/products/show_history_of_stock_take_list/${item.id}`;
    }
  };

  const renderRow = (item, index) => {
    const {
      uniqueStockId,
      categories,
      brands,
      start_date,
      end_date,
      id,
    } = item;

    return (
      <TableRow
        key={`${id}-${index}-${uniqueStockId}`}
        className="table-cell-style"
      >
        <TableCell>{uniqueStockId || ''}</TableCell>
        <TableCell>{categories || ''}</TableCell>
        <TableCell>{brands || ''}</TableCell>
        <TableCell>{start_date || ''}</TableCell>
        <TableCell>{end_date || ''}</TableCell>
        <TableCell align="center">
          <IconButton
            size="small"
            onClick={(event) => handleMenuOpen(event, item)}
            sx={{
              color: "#637381",
              "&:hover": {
                backgroundColor: "rgba(145, 158, 171, 0.08)",
              },
            }}
          >
            <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container justifyContent="space-between" alignItems="center" gap={2}>
              <Grid2 size={{ xs: 12, md: 3}}>
                <Typography className="page-title">
                  Stock Take History
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  placeholder="Search by unique ID, categor ies, brands, or dates..."
                  value={searchQuery}
                  fullWidth
                  size="small"
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                            }}
                            edge="end"
                          >
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4}}>
                <Stack direction={{xs:"column",md:"row"}} spacing={1}>
                  <Button
                    component={Link}
                    to={`${BASE_URL}/stock_take/start_stock_take`}
                    variant="outlined"
          
                    className="button-variant-outlined"
                    fullWidth
                  >
                    Start Stock Take
                  </Button>
                  <Button
                    component={Link}
                    to={`${BASE_URL}/stock_take/stock_take_list`}
                    variant="outlined"
                    className="button-variant-outlined"
                    fullWidth
                  >
                    On Going Stock Take
                  </Button>
                </Stack>
              </Grid2>
            </Grid2>
            </Box>
            <TableContainer
            >
              <Table
                size="small"
                className="table-cell-border"
              >
                <TableHead
                >
                  <TableRow className="table-cell-header">
                    <TableCell width="200px">
                      Unique Id
                    </TableCell>
                    <TableCell width="350px">
                      Categories
                    </TableCell>
                    <TableCell width="180px">
                      Brands
                    </TableCell>
                    <TableCell width="200px">
                      Start Date
                    </TableCell>
                    <TableCell width="200px">
                      End Date
                    </TableCell>
                    <TableCell
                      align="center"
                      width="100px"
                      sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#212b36", letterSpacing: 0.5, py: 1 }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#637381",
                            fontSize: "0.75rem",
                            lineHeight: 1.5,
                          }}
                        >
                          {stockHistory.length === 0
                            ? "No stock take history available."
                            : "No results found matching your search."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredHistory
                      .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                      .map((item, index) => renderRow(item, index))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end" alignItems="center" pt={2}>
              <TablePaginationCustom
                className="pagination-styles"
                page={table.page}
                dense={table.dense}
                count={filteredHistory.length}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
              />
            </Box>
         
        </Paper>

        <Menu
          anchorEl={menuAnchor.el}
          open={Boolean(menuAnchor.el)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            onClick: (e) => e.stopPropagation(),
            sx: {
              minWidth: 200,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <MenuItem onClick={() => handleMenuAction('view', menuAnchor.item)}>
            <ListItemIcon>
              <Iconify icon="eva:eye-fill" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
};

export default StockTakeHistory;
